import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { Label, TextInput } from "components/DesignSystem/TextInput/TextInput";
import { DeleteIcon } from "components/icons/delete";
import { MagnifyingGlass } from "components/icons/MagnifyingGlass";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  MerchantComponent,
  MerchantSelector,
} from "components/Transaction/MerchantSelector";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps, useFormikContext } from "formik";
import { AnimatePresence, motion } from "framer-motion";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useMemo, useState } from "react";
import { SingleValue } from "react-select";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetMerchantByIdQuery } from "store/apis/merchant";
import { useGetExchangeRateQuery } from "store/apis/invoices";
import { useUploadManualTransactionInvoiceMutation } from "store/apis/transactions";
import { BackendError } from "types/utils/error";
import { Preview } from "components/PreviewModal";
import { InternalTransaction as BaseTransaction } from "./AddTransactionManuallyModal";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { DuplicateIcon } from "components/icons/DuplicateIcon";
import ToolTip from "components/design/toolTip";
import randomBytes from "randombytes";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";

type Transaction = BaseTransaction & {
  id: string;
  original_amount?: number;
  exchange_rate?: number;
};

const OriginalAmountCell = ({
  info,
  currency,
}: {
  info: CellContext<Transaction, number | undefined>;
  currency: string;
}) => {
  const { setFieldValue, values } = useFormikContext<{
    transactions: Transaction[];
  }>();
  const id = info.row.index;
  const transaction = values.transactions[id];
  const date = transaction?.date
    ? dayjs(transaction.date).format(YYYY_MM_DD)
    : "";

  const { data: rateData } = useGetExchangeRateQuery(
    { base_currency: "USD", target_currency: currency, date },
    {
      skip:
        !date ||
        !currency ||
        currency === "USD" ||
        Boolean(transaction?.exchange_rate),
    }
  );

  const exchangeRate = transaction?.exchange_rate ?? rateData?.exchange_rate;

  const onValueChange = ({ floatValue }: { floatValue?: number }) => {
    if (!exchangeRate || floatValue === undefined) return;
    const usdEquivalent = floatValue / exchangeRate;
    setFieldValue(`transactions[${id}].exchange_rate`, exchangeRate);
    setFieldValue(
      `transactions[${id}].amount`,
      Number(usdEquivalent.toFixed(2))
    );
  };

  return (
    <div className="t-relative">
      <PriceInput
        aria-label="Amount"
        placeholder="Enter amount"
        name={`transactions[${id}].original_amount`}
        currencyCode={currency}
        showCurrencyFlag
        rightAlign
        onValueChange={onValueChange}
      />
    </div>
  );
};

const UsdEquivalent = ({
  info,
  currency,
}: {
  info: CellContext<Transaction, number>;
  currency: string;
}) => {
  const { values } = useFormikContext<{ transactions: Transaction[] }>();
  const id = info.row.index;
  const transaction = values.transactions[id];
  const date = transaction?.date
    ? dayjs(transaction.date).format(YYYY_MM_DD)
    : "";

  const { data: rateData } = useGetExchangeRateQuery(
    { base_currency: "USD", target_currency: currency, date },
    {
      skip:
        !date ||
        !currency ||
        currency === "USD" ||
        Boolean(transaction?.exchange_rate),
    }
  );

  const exchangeRate = transaction?.exchange_rate ?? rateData?.exchange_rate;

  return (
    <div className="t-relative">
      <PriceInput
        aria-label="USD Equivalent"
        placeholder="120.00"
        name={`transactions[${id}].amount`}
        rightAlign
        prefix="$"
      />
      {exchangeRate && (
        <span className="t-absolute t-top-full t-mt-0.5 t-mb-1 t-text-body-sm-italic t-italic t-text-text-30 t-whitespace-nowrap">
          1.00 USD = {exchangeRate.toFixed(2)} {currency}
        </span>
      )}
    </div>
  );
};

const DateColumn = (info: CellContext<Transaction, string>) => {
  const { setFieldValue } = useFormikContext<{
    transactions: Transaction[];
  }>();
  const { isMobile } = useWindowSize();

  const id = info.row.index;

  const onDateChange = (date: Date) => {
    setFieldValue(
      `transactions[${id}].date`,
      date ? dayjs(date).format(DISPLAY_DATE) : null
    );
  };

  return (
    <Field name={`transactions[${id}].date`}>
      {({ field }: FieldProps) => {
        return (
          <LockedDatePicker
            {...field}
            hideError
            portalId="export_start_date"
            showErrorOnceTouched={false}
            required
            placeholder={DISPLAY_DATE}
            name={`transactions[${id}].date`}
            onDateChange={onDateChange}
            block
            maxDate={new Date()}
            popperPlacement={isMobile ? "bottom-start" : undefined}
          />
        );
      }}
    </Field>
  );
};

const Vendor = (info: CellContext<Transaction, string>) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const { setFieldValue } = useFormikContext<{
    transactions: Transaction[];
  }>();

  const id = info.row.index;
  const vendor = info.getValue();

  const { data, isLoading } = useGetMerchantByIdQuery(
    { groupId, merchantId: vendor || "" },
    { skip: !groupId || !vendor }
  );
  const { logo, name = "" } = data || {};

  const selected = vendor
    ? {
        value: name,
        label: <MerchantComponent logo={logo} name={name} />,
        merchantId: vendor,
        data: name,
      }
    : null;

  const merchantChange = async (values: SingleValue<any>) => {
    setFieldValue(`transactions[${id}].merchant`, values?.merchantId);
  };

  return (
    <MerchantSelector
      isLoading={isLoading}
      isDisabled={isLoading}
      merchantChange={merchantChange}
      withForm
      name={`transactions[${id}].merchant`}
      value={selected}
      creatable={false}
    />
  );
};

export const Description = (info: CellContext<BaseTransaction, string>) => {
  const id = info.row.index;

  return (
    <TextInput
      name={`transactions[${id}].description`}
      placeholder="Enter description"
      hideError
      showErrorOnceTouched
      customSize="small"
    />
  );
};

export const Amount = (info: CellContext<BaseTransaction, number>) => {
  const id = info.row.index;
  const meta = info.table.options.meta as
    | { errors: Record<string, string> }
    | undefined;

  const error = meta?.errors?.[`transactions.${id}.amount`];

  return (
    <PriceInput
      aria-label="Amount"
      placeholder="Enter amount"
      error={error}
      name={`transactions[${id}].amount`}
      rightAlign
      customSize="small"
    />
  );
};

export const Invoice = ({
  info,
  size = "md",
}: {
  info: CellContext<BaseTransaction, Transaction["invoice"]>;
  size?: "sm" | "md";
}) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const rowId = info.row.id;
  const invoice = info.getValue();

  const [uploadManualTransactionInvoice, { isLoading: isUploading }] =
    useUploadManualTransactionInvoiceMutation();

  const { setFieldValue } = useFormikContext<{
    transactions: Transaction[];
  }>();
  const onDrop = async (files: FileType[]) => {
    try {
      const file = await uploadManualTransactionInvoice({
        invoice: files[0] as File,
        entityId,
        groupId,
      }).unwrap();
      setFieldValue(`transactions[${rowId}].invoice`, file);
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };
  const onPreview = () => {
    setPreviewFileId(
      invoice?.file_data?.uuid ? invoice.file_data.uuid : invoice?.uuid || null
    );
  };

  const onInvoiceDelete = () => {
    setFieldValue(`transactions[${rowId}].invoice`, null);
  };

  return (
    <>
      <FileInput
        file={invoice}
        onDrop={onDrop}
        isUploading={isUploading}
        variant="icon"
        onFileClick={onPreview}
        onDelete={onInvoiceDelete}
      />
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};

const Delete = (info: CellContext<Transaction, unknown>) => {
  const { setValues } = useFormikContext<{
    transactions: Transaction[];
  }>();

  const onDelete = () => {
    const newValues = info.table.options.data.filter(
      (_, index) => index !== info.row.index
    );

    setValues({ transactions: newValues });
  };

  return (
    <Button
      customType="ghost_icon"
      onClick={onDelete}
      size="small"
      type="button"
    >
      <ToolTip disableHoverableContent text="Delete">
        <span className="t-text-text-30">
          <DeleteIcon stroke="1" />
        </span>
      </ToolTip>
    </Button>
  );
};

export const TransactionTable = ({
  isCSVTransactions,
  duplicatable = true,
  currency = "USD",
  isNonUSD = false,
}: {
  isCSVTransactions?: boolean;
  duplicatable?: boolean;
  currency?: string;
  isNonUSD?: boolean;
}) => {
  const { setFieldValue, values } = useFormikContext<{
    transactions: Transaction[];
  }>();
  const [showUsdEquivalents, setShowUsdEquivalents] = useState(isNonUSD);

  const { manualTxnInvoiceId } = useSelector(
    (state: RootState) => state.linkInkleInvoice
  );

  const addAnotherTransaction = (values: Transaction[], fromIndex?: number) => {
    const newValues = [
      ...values,
      {
        id: randomBytes(16).toString("hex"),
        date: "",
        merchant: "",
        category: "",
        description: "",
        amount: 0,
        invoice: null,
      },
    ];

    if (fromIndex !== undefined) {
      const copyFrom = values[fromIndex];

      return setFieldValue(
        "transactions",
        values.toSpliced(fromIndex + 1, 0, {
          ...copyFrom,
          // @ts-ignore // Id become never, have to check
          id: randomBytes(16).toString("hex"),
          invoice: null,
        })
      );
    }

    setFieldValue("transactions", newValues);
  };

  const createColumn = createColumnHelper<Transaction>();

  const columns = useMemo(
    () => [
      createColumn.accessor("date", {
        size: 15,
        header: () => <Label required>DATE</Label>,
        cell: DateColumn,
      }),

      ...[
        createColumn.accessor("original_amount", {
          size: 20,
          header: () => <Label required>AMOUNT</Label>,
          cell: (info: CellContext<Transaction, number | undefined>) => (
            <OriginalAmountCell info={info} currency={currency} />
          ),
        }),
        ...(isNonUSD && showUsdEquivalents
          ? [
              createColumn.accessor("amount", {
                size: 15,
                header: () => <Label required>USD EQUIVALENT</Label>,
                cell: (info: CellContext<Transaction, number>) => (
                  <UsdEquivalent info={info} currency={currency} />
                ),
              }),
            ]
          : []),
      ],

      createColumn.accessor("description", {
        size: showUsdEquivalents ? 18 : 22,
        header: () => <Label required>DESCRIPTION</Label>,
        // @ts-ignore // Description not accepting string | number
        cell: Description,
      }),

      createColumn.accessor("merchant", {
        size: 18,
        header: () => <Label>VENDOR</Label>,
        cell: Vendor,
      }),

      createColumn.accessor("category", {
        size: 17,
        header: () => <Label>Category</Label>,
        cell: (info) => {
          const { category = "" } = info.row.original || {};

          return (
            <CategorySelector
              category={category}
              name={`transactions[${info.row.index}].category`}
              isDisabled={Boolean(manualTxnInvoiceId)}
            />
          );
        },
      }),

      createColumn.accessor("invoice", {
        size: 5,
        header: "",
        // @ts-ignore
        cell: (info) => <Invoice info={info} />,
      }),

      ...(duplicatable
        ? [
            createColumn.display({
              id: "copy",
              size: 5,
              header: "",
              cell: (info) => (
                <Button
                  type="button"
                  customType="ghost_icon"
                  onClick={() =>
                    addAnotherTransaction(
                      info.table.options.data,
                      info.row.index
                    )
                  }
                  size="small"
                >
                  <ToolTip disableHoverableContent text="Duplicate">
                    <span className="t-text-text-30">
                      <DuplicateIcon />
                    </span>
                  </ToolTip>
                </Button>
              ),
            }),
          ]
        : []),

      createColumn.display({
        id: "delete",
        size: 5,
        header: "",
        cell: Delete,
      }),
    ],
    [duplicatable, isNonUSD, currency, isCSVTransactions, showUsdEquivalents]
  );

  const table = useReactTable({
    data: values.transactions,
    getRowId: (row) => String(row.id!),
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
  });

  const noTransaction = values.transactions.length === 0;

  return (
    <div className="t-h-full t-overflow-auto">
      {noTransaction ? (
        <EmptyScreen text="No transactions found" className="!t-h-56">
          <span className="t-text-i-neutral-10">
            <MagnifyingGlass size="149" />
          </span>
        </EmptyScreen>
      ) : (
        <div className="t-h-full">
          {isCSVTransactions && (
            <div className="t-text-subtitle-sm t-text-text-60 !t-mb-2">
              Edit transactions if required
            </div>
          )}
          <div className="t-flex t-justify-between t-w-full t-items-center t-mb-2">
            <span className="t-text-body-sm t-text-text-30">
              {values.transactions?.length} Transactions
            </span>

            {isNonUSD && (
              <Checkbox
                checked={showUsdEquivalents}
                onChange={(e) => setShowUsdEquivalents(e.target.checked)}
                label="Show USD equivalents"
                name="showUsdEquivalents"
              />
            )}
          </div>
          <div className="t-h-[280px] t-overflow-auto">
            <Table.Container layout="fixed">
              <Table.Content className="sm:t-min-w-[760px]">
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeadCell
                          key={header.id}
                          style={
                            header.column.columnDef.size !== undefined
                              ? { width: `${header.getSize()}%` }
                              : undefined
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Table.HeadCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  <AnimatePresence initial={false}>
                    {table.getRowModel().rows.map((row) => (
                      <motion.tr
                        layout
                        key={row.id}
                        initial={{ y: -100 }}
                        animate={{ x: 0, y: 0 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ ease: "easeOut", duration: 0.1 }}
                        className="t-border-solid t-border-neutral-0 t-border-b t-border-0 t-text-body-sm"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <Table.Cell
                            key={cell.id}
                            style={
                              cell.column.columnDef.size !== undefined
                                ? { width: `${cell.column.getSize()}%` }
                                : undefined
                            }
                            className={classNames({
                              "t-pb-6": Boolean(row.original.date),
                            })}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Table.Cell>
                        ))}
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </Table.Body>
              </Table.Content>
            </Table.Container>
          </div>
        </div>
      )}
      {!manualTxnInvoiceId && (
        <div className="t-mt-2 t-bg-white t-py-4 t-sticky t-bottom-0">
          <Button
            size="small"
            type="button"
            onClick={() => addAnotherTransaction(values.transactions)}
          >
            Add
            {noTransaction ? " " : " another "}
            transaction
          </Button>
        </div>
      )}
    </div>
  );
};
